import { FC, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Grid, Typography } from "@mui/material";
import ControlledSelectField from "../controlledComponents/ControlledSelectField";
import { useObepListHook } from "../../../hooks/useObepHook";
import {
  useClassByIdHook,
  useClassHook,
  useCourseByIdHook,
  useCourseHook,
  useSectionByIdHook,
  useSectionsByClassHook,
} from "../../../hooks/useAcademicHook";

type HierarchyForm = {
  obepId: string;
  courseId: string;
  classId: string;
  sectionId: string;
};

const emptyHierarchy: HierarchyForm = {
  obepId: "",
  courseId: "",
  classId: "",
  sectionId: "",
};

export type AcademicHierarchySectionPickerProps = {
  /** Emits the selected section UUID for APIs such as `PUT /students/grade-details`. Includes parent class id when a section is selected. */
  onSectionChange: (
    sectionId: string | null,
    context?: { classId: string },
  ) => void;
  /** When editing, hydrate OBEP → course → class from this section id. */
  defaultSectionId?: string | null;
  disabled?: boolean;
};

/**
 * Cascading pickers: OBEP → course (stream) → class → section.
 * Produces a single **sectionId** for student placement and other cross-cutting flows.
 */
const AcademicHierarchySectionPicker: FC<AcademicHierarchySectionPickerProps> = ({
  onSectionChange,
  defaultSectionId,
  disabled,
}) => {
  const { control, watch, setValue, reset } = useForm<HierarchyForm>({
    defaultValues: emptyHierarchy,
  });

  const obepId = watch("obepId");
  const courseId = watch("courseId");
  const classId = watch("classId");
  const sectionId = watch("sectionId");

  const prevObep = useRef<string | undefined>(undefined);
  const prevCourse = useRef<string | undefined>(undefined);
  const prevClass = useRef<string | undefined>(undefined);

  const { data: obeps } = useObepListHook({
    page: 1,
    limit: 500,
  });
  const { data: courses } = useCourseHook();
  const { data: classes } = useClassHook();
  const { data: sections } = useSectionsByClassHook(classId || undefined);

  const { data: presetSection } = useSectionByIdHook(
    defaultSectionId ?? undefined,
  );
  const { data: presetClass } = useClassByIdHook(presetSection?.classId);
  const { data: presetCourse } = useCourseByIdHook(presetClass?.courseId);

  const obepOptions = useMemo(
    () =>
      obeps?.map((o) => ({
        key: o.obepId,
        value: o.obepName,
      })) ?? [],
    [obeps],
  );

  const courseOptions = useMemo(() => {
    if (!obepId)
      return [] as { key: string; value: string }[];
    return (
      courses?.filter((c) => c.obepId === obepId).map((c) => ({
        key: c.id,
        value: c.name,
      })) ?? []
    );
  }, [courses, obepId]);

  const classOptions = useMemo(() => {
    if (!courseId)
      return [] as { key: string; value: string }[];
    return (
      classes?.filter((c) => c.courseId === courseId).map((c) => ({
        key: c.id,
        value: `${c.name} (grade ${c.grade}, ${c.academicYear})`,
      })) ?? []
    );
  }, [classes, courseId]);

  const sectionOptions = useMemo(
    () =>
      sections?.map((s) => ({
        key: s.id,
        value: s.name,
      })) ?? [],
    [sections],
  );

  useEffect(() => {
    if (!defaultSectionId) {
      prevObep.current = undefined;
      prevCourse.current = undefined;
      prevClass.current = undefined;
      reset(emptyHierarchy);
      return;
    }
    if (!presetSection || !presetClass || !presetCourse) {
      return;
    }
    prevObep.current = undefined;
    prevCourse.current = undefined;
    prevClass.current = undefined;
    reset({
      obepId: presetCourse.obepId,
      courseId: presetCourse.id,
      classId: presetClass.id,
      sectionId: presetSection.id,
    });
  }, [defaultSectionId, presetSection, presetClass, presetCourse, reset]);

  useEffect(() => {
    if (prevObep.current === undefined) {
      prevObep.current = obepId;
      return;
    }
    if (prevObep.current !== obepId) {
      setValue("courseId", "");
      setValue("classId", "");
      setValue("sectionId", "");
    }
    prevObep.current = obepId;
  }, [obepId, setValue]);

  useEffect(() => {
    if (prevCourse.current === undefined) {
      prevCourse.current = courseId;
      return;
    }
    if (prevCourse.current !== courseId) {
      setValue("classId", "");
      setValue("sectionId", "");
    }
    prevCourse.current = courseId;
  }, [courseId, setValue]);

  useEffect(() => {
    if (prevClass.current === undefined) {
      prevClass.current = classId;
      return;
    }
    if (prevClass.current !== classId) {
      setValue("sectionId", "");
    }
    prevClass.current = classId;
  }, [classId, setValue]);

  const onSectionChangeRef = useRef(onSectionChange);
  useLayoutEffect(() => {
    onSectionChangeRef.current = onSectionChange;
  });
  useEffect(() => {
    onSectionChangeRef.current(
      sectionId ? sectionId : null,
      sectionId && classId ? { classId } : undefined,
    );
  }, [sectionId, classId]);

  const emptyOption = { key: "", value: "—" };

  const pickerDisabled = !!disabled;

  return (
    <Box
      data-testid="academic-hierarchy-section-picker"
      sx={
        pickerDisabled
          ? { opacity: 0.55, pointerEvents: "none" }
          : undefined
      }
      aria-disabled={pickerDisabled || undefined}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Section (OBEP → course → class → section)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <ControlledSelectField
            control={control}
            name="obepId"
            id="hierarchy-obep"
            label="OBEP / board policy"
            itemList={[emptyOption, ...obepOptions]}
            isGridItem={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelectField
            control={control}
            name="courseId"
            id="hierarchy-course"
            label="Course (stream)"
            itemList={[emptyOption, ...courseOptions]}
            isGridItem={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelectField
            control={control}
            name="classId"
            id="hierarchy-class"
            label="Class"
            itemList={[emptyOption, ...classOptions]}
            isGridItem={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelectField
            control={control}
            name="sectionId"
            id="hierarchy-section"
            label="Section"
            itemList={[emptyOption, ...sectionOptions]}
            isGridItem={false}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AcademicHierarchySectionPicker;
